import { QueryClient } from '@tanstack/react-query';
import { medicationApi } from 'src/api/medication';
import { ORGANIZATION_HEADER } from 'src/sections/@dashboard/warehouse/utils/request';
import { MedicationDosingSuggestionDto } from 'woofwoof-api';

/** Which drug the vet picked, for which patient, in which clinic (#692). */
export interface DosingSuggestionRequest {
  organizationId: string;
  /** The patient the drug is for. */
  profileId: string;
  /** Wins over the name when the drug has one. */
  medicineProductId?: string;
  name: string;
}

/**
 * A clinic's usual dose rarely moves; one read per drug and patient a minute.
 * A medication this browser saves refreshes it at once
 * (`invalidateDosingSuggestions`).
 */
export const DOSING_SUGGESTION_STALE_MS = 60_000;

const DOSING_SUGGESTION_KEY = 'medication-dosing-suggestion';

/**
 * Key `['medication-dosing-suggestion', organizationId, profileId, drug]`:
 * the organization right after the prefix, so a clinic switch never answers
 * with the previous clinic's history.
 */
export function dosingSuggestionKey(
  request: DosingSuggestionRequest,
): readonly unknown[] {
  return [
    DOSING_SUGGESTION_KEY,
    request.organizationId,
    request.profileId,
    request.medicineProductId
      ? `id:${request.medicineProductId}`
      : `name:${request.name.trim().toLocaleLowerCase('pl-PL')}`,
  ];
}

/**
 * How the clinic last dosed the drug, or null when it never did (204). The
 * request names the key's organization itself (like the Warehouse reads).
 */
export async function fetchDosingSuggestion(
  request: DosingSuggestionRequest,
  signal?: AbortSignal,
): Promise<MedicationDosingSuggestionDto | null> {
  const response =
    await medicationApi.medicationDosingSuggestionControllerSuggest(
      {
        profileId: request.profileId,
        ...(request.medicineProductId
          ? { medicineProductId: request.medicineProductId }
          : { name: request.name.trim() }),
      },
      {
        headers: { [ORGANIZATION_HEADER]: request.organizationId },
        ...(signal ? { signal } : {}),
      },
    );
  return response.status === 204 || !response.data ? null : response.data;
}

/**
 * A saved Facility medication may now be its clinic's last dose of the drug,
 * for any of the clinic's patients: the next pick reads that clinic's
 * history afresh.
 */
export function invalidateDosingSuggestions(
  queryClient: QueryClient,
  organizationId: string,
): void {
  void queryClient.invalidateQueries([DOSING_SUGGESTION_KEY, organizationId]);
}
